import { useCallback, useEffect, useState } from "react";
import { Plus, Printer, Trash2, X, Settings, Loader2, Pencil, Download } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import { useReadOnly } from "../access/ReadOnly";
import {
    silentPrintBarcode,
    silentPrint,
    buildNamedBarcodeLabel,
    browserPrint,
    downloadLabelsPdf,
    getSavedLocalPrinter,
} from "./helpers/printAgent";
import {
    submitNetworkPrintJob,
    getSavedNetworkPrinter,
} from "./helpers/networkPrint";
import PrinterSelectModal from "./PrinterSelectModal";
import { apiFetch } from "../api/apiFetch";

// Settings › Dispatch › Courier pallets. One entry per physical pallet /
// cage at the conveyor unload point, each bound to ONE courier. The
// Conveyor Unload page names the courier for a scanned parcel and the
// operator confirms by scanning the pallet's barcode (migration 218).
// Mirrors DesksAdmin: same storage shape, barcode allocation and label
// printing, plus a courier picker.
//
// "Print upon close" (2026-10-19, migration 226): a per-warehouse switch —
// when on, closing a pallet at the unload point prints its 6x4 label on
// the handheld straight away; when off a team leader prints it from
// Outbound. Poland first; Pembroke and Newport stay off for now.

interface Bay {
    name: string;
    barcode: string;
    carrier_code: string;
    // Which of the courier's parcels this pallet takes: APC pre-sort
    // numbers (the number in the black block on the label), InPost 24 /
    // 48 / LOCKER. Empty = the courier's catch-all pallet.
    sort_codes?: string[];
}

const codesText = (b: Bay) => (b.sort_codes ?? []).join(", ");

interface AllSettingsRow {
    warehouse: string;
    dispatch_carrier_bays?: Bay[];
}

// The couriers a pallet can be bound to. Codes match carrier_bookings
// .carrier_code (BE carriers/adapters/registry.js); 'own_label' is a
// parcel shipped on a label the Portal did not book.
export const BAY_CARRIERS: Array<{ code: string; name: string }> = [
    { code: "apc", name: "APC" },
    { code: "inpost", name: "InPost" },
    { code: "dpd", name: "DPD" },
    // Royal Mail (Pembroke, 2026-10-20): never booked through the Portal —
    // the unload screen recognises its S10 tracking number by shape.
    { code: "royal_mail", name: "Royal Mail" },
    // Collection (2026-10-20): the collection shelf. Collection orders'
    // parcels are confirmed onto it at the unload point; no pallets.
    { code: "collection", name: "Collection" },
    { code: "cargus", name: "Cargus" },
    { code: "palletways", name: "Palletways" },
    { code: "own_label", name: "Own label" },
];
const carrierName = (code: string) => BAY_CARRIERS.find((c) => c.code === code)?.name ?? code;

const printOneLabel = async (barcode: string, name: string): Promise<{ success: boolean; error?: string }> => {
    const html = buildNamedBarcodeLabel(barcode, name);
    const saved = getSavedNetworkPrinter();
    if (saved) {
        const result = await submitNetworkPrintJob(saved.agentId, saved.printerName, html, 1, barcode, false, { name });
        if (result.success) return { success: true };
    }
    const zebra = await silentPrintBarcode(barcode, 1, {
        name,
        fallbackHtml: html,
        printer: getSavedLocalPrinter() ?? undefined,
    });
    if (zebra.success) return { success: true };
    const local = await silentPrint(html, 1, getSavedLocalPrinter() ?? undefined);
    if (local.success) return { success: true };
    return browserPrint(html);
};

const CarrierBaysAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [warehouses, setWarehouses] = useState<string[]>([]);
    const [baysByWarehouse, setBaysByWarehouse] = useState<Record<string, Bay[]>>({});
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState<{ warehouse: string; name: string; carrier_code: string; sort_codes: string } | null>(null);
    const [editing, setEditing] = useState<{ warehouse: string; barcode: string; name: string; carrier_code: string; sort_codes: string } | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [showPrinterSelect, setShowPrinterSelect] = useState(false);
    const [removeConfirm, setRemoveConfirm] = useState<{ warehouse: string; bay: Bay } | null>(null);
    const [removing, setRemoving] = useState(false);
    const [printingBarcode, setPrintingBarcode] = useState<string | null>(null);
    const [printingAllWh, setPrintingAllWh] = useState<string | null>(null);
    const [downloadingWh, setDownloadingWh] = useState<string | null>(null);
    const [printerNeedsSetup, setPrinterNeedsSetup] = useState(false);
    // Per warehouse: { enabled, available } — available is false until
    // migration 226 is applied, and the switch is then disabled.
    const [printOnClose, setPrintOnClose] = useState<Record<string, { enabled: boolean; available: boolean }>>({});
    const [savingPrintOnClose, setSavingPrintOnClose] = useState<string | null>(null);

    const loadPrintOnClose = useCallback(async (whList: string[]) => {
        const next: Record<string, { enabled: boolean; available: boolean }> = {};
        await Promise.all(whList.map(async (wh) => {
            try {
                const res = await apiFetch(`/dispatch-admin/carrier-bays/print-on-close?warehouse=${encodeURIComponent(wh)}`, {});
                if (!res.ok) return;
                const data = await res.json();
                next[wh] = { enabled: data.enabled === true, available: data.available !== false };
            } catch {
                /* the switch just doesn't render for that warehouse */
            }
        }));
        setPrintOnClose(next);
    }, []);

    const togglePrintOnClose = async (warehouse: string, enabled: boolean) => {
        if (readOnly) return;
        setSavingPrintOnClose(warehouse);
        try {
            const res = await apiFetch(`/dispatch-admin/carrier-bays/print-on-close`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse, enabled }),
            });
            if (!res.ok) throw new Error("print-on-close");
            const data = await res.json();
            setPrintOnClose((m) => ({ ...m, [warehouse]: { enabled: data.enabled === true, available: true } }));
        } catch {
            toast.error(t("server_error"), { toastId: "print-on-close-err" });
        } finally {
            setSavingPrintOnClose(null);
        }
    };

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`/settings`, {});
            if (!res.ok) throw new Error("settings");
            const rows: AllSettingsRow[] = await res.json();
            const whList = rows.map(r => r.warehouse).sort();
            const map: Record<string, Bay[]> = {};
            for (const r of rows) map[r.warehouse] = r.dispatch_carrier_bays ?? [];
            setWarehouses(whList);
            setBaysByWarehouse(map);
            loadPrintOnClose(whList);
        } catch {
            toast.error(t("server_error"), { toastId: "bays-load-err" });
        } finally {
            setLoading(false);
        }
    }, [t, loadPrintOnClose]);

    useEffect(() => { refresh(); }, [refresh]);

    const post = async (path: string, body: unknown) => {
        const res = await apiFetch(path, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(path);
    };

    const handleAdd = async () => {
        if (!adding || !adding.name.trim() || !adding.carrier_code || readOnly) return;
        setSubmitting(true);
        try {
            await post(`/dispatch-admin/carrier-bays/add`, {
                warehouse: adding.warehouse, name: adding.name.trim(), carrier_code: adding.carrier_code, sort_codes: adding.sort_codes,
            });
            await refresh();
            setAdding(null);
        } catch {
            toast.error(t("server_error"), { toastId: "bay-add-err" });
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditSave = async () => {
        if (!editing || !editing.name.trim() || !editing.carrier_code || readOnly) return;
        setSubmitting(true);
        try {
            await post(`/dispatch-admin/carrier-bays/update`, {
                warehouse: editing.warehouse, barcode: editing.barcode, name: editing.name.trim(), carrier_code: editing.carrier_code,
                sort_codes: editing.sort_codes,
            });
            await refresh();
            setEditing(null);
        } catch {
            toast.error(t("server_error"), { toastId: "bay-update-err" });
        } finally {
            setSubmitting(false);
        }
    };

    const confirmRemove = async () => {
        if (!removeConfirm || readOnly) return;
        setRemoving(true);
        try {
            await post(`/dispatch-admin/carrier-bays/remove`, { warehouse: removeConfirm.warehouse, barcode: removeConfirm.bay.barcode });
            await refresh();
            setRemoveConfirm(null);
        } catch {
            toast.error(t("server_error"), { toastId: "bay-remove-err" });
        } finally {
            setRemoving(false);
        }
    };

    const handlePrint = async (bay: Bay) => {
        setPrintingBarcode(bay.barcode);
        try {
            const result = await printOneLabel(bay.barcode, bay.name);
            if (!result.success) {
                setPrinterNeedsSetup(true);
                toast.error(t("print_failed"), { toastId: "bay-print-err" });
            }
        } finally {
            setPrintingBarcode(null);
        }
    };

    const handlePrintAll = async (warehouse: string) => {
        const bays = baysByWarehouse[warehouse] ?? [];
        if (bays.length === 0) return;
        setPrintingAllWh(warehouse);
        try {
            let failures = 0;
            for (const b of bays) {
                const r = await printOneLabel(b.barcode, b.name);
                if (!r.success) failures += 1;
            }
            if (failures > 0) {
                setPrinterNeedsSetup(true);
                toast.warn(t("print_partial_failed", { failed: String(failures), total: String(bays.length) }));
            }
        } finally {
            setPrintingAllWh(null);
        }
    };

    const handleDownloadAll = async (warehouse: string) => {
        const bays = baysByWarehouse[warehouse] ?? [];
        if (bays.length === 0) return;
        setDownloadingWh(warehouse);
        try {
            const result = await downloadLabelsPdf(
                bays.map(b => ({ barcode: b.barcode, name: b.name })),
                `courier-pallets-${warehouse}.pdf`,
            );
            if (!result.success) {
                toast.error(result.error || t("download_failed"), { toastId: "bay-pdf-err" });
            }
        } finally {
            setDownloadingWh(null);
        }
    };

    const carrierSelect = (value: string, onChange: (v: string) => void) => (
        <select className="dispatch-admin-form-input" value={value} onChange={e => onChange(e.target.value)}>
            <option value="">—</option>
            {BAY_CARRIERS.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
    );

    if (loading) return <div className="admin-settings-loading">{t("loading")}</div>;

    return (
        <div>
            <div className="dispatch-admin-toolbar">
                <button
                    type="button"
                    className={`btn btn-ghost btn-sm ${printerNeedsSetup ? "dispatch-admin-printer-needed" : ""}`}
                    onClick={() => { setShowPrinterSelect(true); setPrinterNeedsSetup(false); }}
                >
                    <Settings size={14} />
                    {t("printer_select")}
                </button>
            </div>

            {warehouses.length === 0 && (
                <div className="admin-settings-empty">{t("no_warehouses")}</div>
            )}

            {warehouses.map(wh => {
                const bays = baysByWarehouse[wh] ?? [];
                return (
                    <details key={wh} className="admin-settings-po-warehouse">
                        <summary className="admin-settings-po-warehouse-summary">
                            <span className="admin-settings-po-warehouse-name">{wh}</span>
                            <span className="admin-settings-po-warehouse-count">{bays.length}</span>
                        </summary>
                        <div className="admin-settings-po-editor">
                            {printOnClose[wh] && (
                                <label className="dispatch-admin-switch-row" data-testid={`print-on-close-${wh}`}>
                                    <span className="dispatch-admin-switch-text">
                                        <span className="dispatch-admin-switch-title">{t("print_on_close_title")}</span>
                                        <span className="admin-settings-section-hint">
                                            {printOnClose[wh].available ? t("print_on_close_hint") : t("print_on_close_unavailable")}
                                        </span>
                                    </span>
                                    <input
                                        type="checkbox"
                                        role="switch"
                                        aria-label={`${t("print_on_close_title")} ${wh}`}
                                        checked={printOnClose[wh].enabled}
                                        disabled={readOnly || !printOnClose[wh].available || savingPrintOnClose === wh}
                                        onChange={(e) => togglePrintOnClose(wh, e.target.checked)}
                                    />
                                </label>
                            )}
                            <div className="dispatch-admin-rows">
                                {bays.map(b => (
                                    <div key={b.barcode} className="dispatch-admin-row dispatch-admin-row--bay">
                                        <span className="dispatch-admin-row-name">{b.name}</span>
                                        <span className="dispatch-admin-row-carrier">
                                            {carrierName(b.carrier_code)}
                                            {(b.sort_codes ?? []).length > 0 && <span className="dispatch-admin-row-codes"> · {codesText(b)}</span>}
                                        </span>
                                        <span className="dispatch-admin-row-barcode">{b.barcode}</span>
                                        <div className="dispatch-admin-row-actions-inline">
                                            <button
                                                type="button"
                                                className="dispatch-admin-row-icon"
                                                onClick={() => setEditing({ warehouse: wh, barcode: b.barcode, name: b.name, carrier_code: b.carrier_code, sort_codes: codesText(b) })}
                                                disabled={readOnly}
                                                aria-disabled={readOnly}
                                                title={readOnly ? t("read_only_mode") : t("edit")}
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                type="button"
                                                className="dispatch-admin-row-icon"
                                                onClick={() => handlePrint(b)}
                                                title={t("print_label")}
                                                disabled={printingBarcode === b.barcode || printingAllWh === wh}
                                            >
                                                {printingBarcode === b.barcode || printingAllWh === wh
                                                    ? <Loader2 size={16} className="dispatch-admin-spin" />
                                                    : <Printer size={16} />}
                                            </button>
                                            <button
                                                type="button"
                                                className="dispatch-admin-row-icon dispatch-admin-row-icon--danger"
                                                onClick={() => setRemoveConfirm({ warehouse: wh, bay: b })}
                                                disabled={readOnly}
                                                aria-disabled={readOnly}
                                                title={readOnly ? t("read_only_mode") : t("remove_bay")}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {bays.length === 0 && (
                                    <div className="admin-settings-empty">{t("no_bays_yet")}</div>
                                )}
                            </div>
                            <div className="dispatch-admin-row-actions">
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => setAdding({ warehouse: wh, name: "", carrier_code: "", sort_codes: "" })}
                                    disabled={readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    <Plus size={14} />
                                    {t("add_bay")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => handlePrintAll(wh)}
                                    disabled={bays.length === 0 || printingAllWh === wh}
                                >
                                    {printingAllWh === wh
                                        ? <Loader2 size={14} className="dispatch-admin-spin" />
                                        : <Printer size={14} />}
                                    {t("print_all")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => handleDownloadAll(wh)}
                                    disabled={bays.length === 0 || downloadingWh === wh}
                                >
                                    {downloadingWh === wh
                                        ? <Loader2 size={14} className="dispatch-admin-spin" />
                                        : <Download size={14} />}
                                    {t("download_all")}
                                </button>
                            </div>
                        </div>
                    </details>
                );
            })}

            {adding && (
                <div className="modal-overlay" onClick={() => !submitting && setAdding(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 360 }}>
                        <div className="modal-card-header">
                            <h2>{t("add_bay")} — {adding.warehouse}</h2>
                            <button className="modal-close" onClick={() => setAdding(null)} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <label className="dispatch-admin-form-label">
                                {t("bay_name")}
                                <input
                                    type="text"
                                    autoFocus
                                    className="dispatch-admin-form-input"
                                    value={adding.name}
                                    onChange={e => setAdding({ ...adding, name: e.target.value })}
                                    onKeyDown={e => { if (e.key === "Enter") handleAdd(); }}
                                    placeholder="APC PALLET"
                                />
                            </label>
                            <label className="dispatch-admin-form-label" style={{ marginTop: 12 }}>
                                {t("bay_carrier")}
                                {carrierSelect(adding.carrier_code, v => setAdding({ ...adding, carrier_code: v }))}
                            </label>
                            <label className="dispatch-admin-form-label" style={{ marginTop: 12 }}>
                                {t("bay_sort_codes")}
                                <input
                                    type="text"
                                    className="dispatch-admin-form-input"
                                    value={adding.sort_codes}
                                    onChange={e => setAdding({ ...adding, sort_codes: e.target.value })}
                                    placeholder="1, 2"
                                />
                                <span className="admin-settings-section-hint">{t("bay_sort_codes_hint")}</span>
                            </label>
                            <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                                <button className="btn btn-ghost" onClick={() => setAdding(null)} disabled={submitting}>
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-success"
                                    onClick={handleAdd}
                                    disabled={submitting || !adding.name.trim() || !adding.carrier_code || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {submitting ? t("submitting") : t("add_bay")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {editing && (
                <div className="modal-overlay" onClick={() => !submitting && setEditing(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
                        <div className="modal-card-header">
                            <h2>{t("edit_bay")} — {editing.barcode}</h2>
                            <button className="modal-close" onClick={() => setEditing(null)} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <label className="dispatch-admin-form-label">
                                {t("bay_name")}
                                <input
                                    type="text"
                                    className="dispatch-admin-form-input"
                                    value={editing.name}
                                    onChange={e => setEditing({ ...editing, name: e.target.value })}
                                />
                            </label>
                            <label className="dispatch-admin-form-label" style={{ marginTop: 12 }}>
                                {t("bay_carrier")}
                                {carrierSelect(editing.carrier_code, v => setEditing({ ...editing, carrier_code: v }))}
                            </label>
                            <label className="dispatch-admin-form-label" style={{ marginTop: 12 }}>
                                {t("bay_sort_codes")}
                                <input
                                    type="text"
                                    className="dispatch-admin-form-input"
                                    value={editing.sort_codes}
                                    onChange={e => setEditing({ ...editing, sort_codes: e.target.value })}
                                    placeholder="1, 2"
                                />
                                <span className="admin-settings-section-hint">{t("bay_sort_codes_hint")}</span>
                            </label>
                            <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                                <button className="btn btn-ghost" onClick={() => setEditing(null)} disabled={submitting}>
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-success"
                                    onClick={handleEditSave}
                                    disabled={submitting || !editing.name.trim() || !editing.carrier_code || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {submitting ? t("submitting") : t("save")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {removeConfirm && (
                <div className="modal-overlay" onClick={() => !removing && setRemoveConfirm(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
                        <div className="modal-card-header">
                            <h2>{t("remove_bay")}</h2>
                            <button className="modal-close" onClick={() => setRemoveConfirm(null)} disabled={removing}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p style={{ marginBottom: 16, lineHeight: 1.5 }}>
                                {t("confirm_remove_bay", { name: removeConfirm.bay.name })}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button className="btn btn-ghost" onClick={() => setRemoveConfirm(null)} disabled={removing}>
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-danger"
                                    onClick={confirmRemove}
                                    disabled={removing || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {removing ? t("submitting") : t("remove_bay")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showPrinterSelect && (
                <PrinterSelectModal
                    autoPrint={true}
                    printMode={null}
                    onSelect={() => setShowPrinterSelect(false)}
                    onConnectLocal={() => setShowPrinterSelect(false)}
                    onDisconnect={() => setShowPrinterSelect(false)}
                    onClose={() => setShowPrinterSelect(false)}
                />
            )}
        </div>
    );
};

export default CarrierBaysAdmin;
